<template>
  <simple-scrollable-modal>
    <template #title>
      <h4>Disponibilidad del item</h4>
    </template>
    <template #content>
      <v-checkbox
        class="mt-0"
        label="Sólo mostrar en determinados días y horarios"
        :input-value="hourRanges !== null"
        @change="toggleHours"
      />
      <template v-if="hourRanges !== null">
        <v-row v-for="day of days" :key="day.val" class="flex-md-nowrap">
          <v-col
            cols="12"
            md="auto"
            :class="{ 'py-1': !hourRangesByDay.hasOwnProperty(day.val) }"
          >
            <v-checkbox
              :label="$t(day.name)"
              :input-value="hourRangesByDay.hasOwnProperty(day.val)"
              @change="
                !hourRangesByDay[day.val]
                  ? addRange(day.val)
                  : clearRanges(day.val)
              "
              class="mt-0"
              style="min-width: 100px"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="auto" class="flex-fill flex-nowrap">
            <div
              class="d-flex flex-row align-center"
              v-for="(range, index) of hourRangesByDay[day.val]"
              :key="index"
            >
              <v-range-slider
                class="d-flex flex-column pr-5"
                color=""
                :value="[labelToVal[range.from], labelToVal[range.to]]"
                @change="setRange(range, $event)"
                min="0"
                hide-details
                thumb-label="always"
                :max="timeCount"
              >
                <template v-slot:thumb-label="{ value }">
                  {{ timeLabels[value].substr(0, 5) }}
                </template>
              </v-range-slider>
              <v-btn
                @click="removeRange(range)"
                class="d-flex flex-column"
                icon
                color="red"
              >
                <v-icon>remove</v-icon>
              </v-btn>
              <v-btn
                v-if="index < hourRangesByDay[day.val].length - 1"
                disabled
                class="d-flex flex-column"
                icon
                style="width: 32px; height: 32px"
                color="success"
              >
                <v-icon>add</v-icon>
              </v-btn>
              <v-btn
                v-else
                @click="addRange(day.val)"
                class="d-flex flex-column"
                fab
                x-small
                color="success"
              >
                <v-icon>add</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>
    </template>
    <template #action-bar>
      <div class="d-flex flex-row align-center justify-end">
        <v-btn text :disabled="loading" @click="$emit('close')">Cancelar</v-btn>
        <v-btn color="primary" @click="save" :loading="loading">Guardar</v-btn>
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { Globals } from '../../modules/Globals';
  import { IHourRange } from '../../interfaces/IHourRange';
  import { IItem } from '../../interfaces/IItem';
  import { ICategory } from '../../interfaces/ICategory';
  import { ISimpleHourRange } from '../../interfaces/ISimpleHourRange';
  import SimpleScrollableModal from './base/SimpleScrollableModal.vue';
  import { vueData } from '../../VueData';
  import ItemsService from '../../api/services/items.service';
  import CategoriesService from '../../api/services/categories.service';
  import { AlertType } from '../../interfaces/IAlert';

  @Component({
    components: { SimpleScrollableModal },
  })
  export default class HourRangesModal extends Vue {
    @Prop() item: IItem | ICategory;
    @Prop({ required: true }) type: 'item' | 'category';
    itemsService = new ItemsService();
    categoriesService = new CategoriesService();

    days = Globals.days;

    hourRanges: ISimpleHourRange[] = null;
    timeLabels = [];
    minuteInterval = 15;
    timeCount = 0;

    changedTimes = false;
    loading = false;

    labelToVal = {};

    async created() {
      this.setUpTimes();

      if (this.item.hourRanges) {
        this.hourRanges = [];
        for (const hour of this.item.hourRanges)
          this.hourRanges.push({ ...hour }); // Meto una copia de cada hourrange para poder modificar sin cambiar el original
      }
    }

    toggleHours() {
      this.hourRanges = this.hourRanges === null ? [] : null;
    }

    addRange(day: number) {
      let from = '00:00:00',
        to = '23:59:00';

      if (this.hourRanges.length) {
        const last = this.hourRanges[this.hourRanges.length - 1];
        from = last.from;
        to = last.to;
      }

      this.hourRanges.push({
        day,
        from,
        to,
      });
      this.changedTimes = true;
    }

    clearRanges(day: number) {
      for (let i = this.hourRanges.length; --i >= 0; ) {
        if (this.hourRanges[i].day === day) this.hourRanges.splice(i, 1);
      }
      this.changedTimes = true;
    }

    setRange(range: IHourRange, data) {
      range.from = this.timeLabels[data[0]];
      range.to = this.timeLabels[data[1]];
      this.changedTimes = true;
    }

    removeRange(range: IHourRange) {
      this.hourRanges.splice(this.hourRanges.indexOf(range), 1);
      this.changedTimes = true;
    }

    get hourRangesByDay() {
      const rangesByDay: { [day: number]: IHourRange[] } = {};

      for (const range of this.hourRanges) {
        let ranges;
        if (!(ranges = rangesByDay[range.day]))
          ranges = rangesByDay[range.day] = [];

        ranges.push(range);
      }

      return rangesByDay;
    }

    setUpTimes() {
      const subCount = 60 / this.minuteInterval;
      const count = (this.timeCount = (24 * 60) / this.minuteInterval);

      function twoDigits(val: number) {
        return ('0' + val).slice(-2);
      }

      for (let i = 0; i < 24; i++) {
        for (let j = 0; j < subCount; j++) {
          this.timeLabels.push(
            `${twoDigits(i)}:${twoDigits(j * this.minuteInterval)}:00`
          );
          this.labelToVal[
            `${twoDigits(i)}:${twoDigits(j * this.minuteInterval)}:00`
          ] = i * 4 + j;
        }
      }
      this.timeLabels.push(`23:59:00`);
      this.labelToVal[`23:59:00`] = count;
    }

    async save() {
      try {
        this.loading = true;
        const hours = this.hourRanges?.length ? this.hourRanges : null;
        if (this.type === 'item') {
          await this.itemsService.saveHours(this.item.id, hours);
        } else {
          await this.categoriesService.saveHours(this.item.id, hours);
        }
        vueData.addAlert('Configuración guardada', AlertType.Success);
        this.$parent.$emit('saved', hours);
        this.$emit('close');
      } catch (e) {
        vueData.serverError(e);
        console.error(e);
      } finally {
        this.loading = false;
      }
    }
  }
</script>
